<template>
  <div>

    <!-- ====================== FILTRES ====================== -->
    <b-card class="card-custom">
      <b-row>
        <b-col md="3">
          <b-form-group label="Date, du">
            <Input type="date" v-model="filters.start"/>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="au">
            <Input type="date" v-model="filters.end"/>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Conseiller">
            <Input type="select-model" v-model="filters.user_id" :options="$api.params.User" placeholder="Tous"/>
          </b-form-group>
        </b-col>
        <!-- Pas de <b-form-group> ici : son `label` est rendu en texte brut, une
             espace insécable y apparaîtrait telle quelle. `mb-3` reproduit la
             marge basse du groupe pour garder le bouton aligné sur les champs. -->
        <b-col md="2" class="d-flex align-items-end">
          <b-button variant="primary" block class="mb-3" @click="init()">
            <span v-if="load === false">Rechercher</span>
            <i v-else class="fas fa-spin fa-spinner"></i>
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12">
          <b-button
            v-for="p in presets" :key="p.key"
            size="sm" class="mr-2"
            :variant="isPreset(p) ? 'primary' : 'light-info'"
            @click="applyPreset(p)">{{ p.titre }}</b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Le back refuse start/end à null : on ne l'appelle pas pour rien. -->
    <b-alert :show="dateError !== null" variant="danger" class="mt-3">{{ dateError }}</b-alert>

    <!-- Filtre conseiller actif : rappelé ici parce qu'un clic dans l'onglet
         Conseillers le pose sans passer par le champ du haut. -->
    <b-alert :show="conseillersChoisis.length > 0" variant="light" class="mt-3 mb-0 filtre-actif">
      <i class="fas fa-filter mr-2"></i>
      Page filtrée sur <strong>{{ conseillersChoisis.join(', ') }}</strong>
      <b-button size="sm" variant="link" class="p-0 ml-2" @click="filters.user_id = []">Voir toute l'équipe</b-button>
    </b-alert>

    <!-- ====================== KPI GLOBAUX ====================== -->
    <div class="kpi-grille mt-3">
      <b-card v-for="k in kpis" :key="k.titre" class="card-custom text-center kpi-carte">
        <div class="text-muted text-uppercase kpi-titre">{{ k.titre }}</div>
        <div class="kpi-valeur" :class="'text-' + k.color">{{ k.valeur }}</div>
        <div class="text-muted kpi-detail">{{ k.detail }}</div>
        <div v-if="k.delta" class="kpi-delta" :class="k.delta.classe" v-b-tooltip.hover :title="k.delta.tooltip">
          <i class="mr-1" :class="k.delta.icone"></i>{{ k.delta.texte }}
        </div>
        <div v-else class="kpi-delta text-muted">—</div>
      </b-card>
    </div>
    <div v-if="periodePrec" class="text-muted comparaison-legende">
      <span v-if="eventsPrec !== null">
        Écarts calculés par rapport à la période précédente de même durée,
        du {{ formatJour(periodePrec.start) }} au {{ formatJour(periodePrec.end) }}.
        Taux en points, volumes et délai en pourcentage.
      </span>
      <span v-else-if="load === false">
        La période précédente n'a pas pu être chargée : pas de comparaison.
      </span>
    </div>

    <!-- ====================== ONGLETS ====================== -->
    <b-tabs v-model="onglet" pills class="mt-3" nav-class="onglets">

      <!-- ===================== ÉVÉNEMENTS ===================== -->
      <b-tab>
        <template #title><i class="fas fa-stream mr-2"></i>Événements</template>

        <!-- Ouvertures sans lien tracé : anomalie de tracking, pas une stat. -->
        <b-alert :show="stats.ouvertSansLien > 0" variant="warning" class="mt-3">
          <strong>{{ stats.ouvertSansLien }}</strong> prospect(s) ont ouvert le parcours sans qu'aucun
          <em>lien_envoyee</em> ne soit enregistré sur la période. Soit le lien est parti avant la période
          affichée, soit l'événement d'envoi n'a pas été tracé.
        </b-alert>

        <b-row>
          <!-- FUNNEL -->
          <b-col lg="5">
            <b-card class="card-custom mt-3" title="Parcours, étape par étape">
              <div class="text-muted mb-4 sous-titre">
                Prospects distincts ayant atteint chaque étape. Le pourcentage compare à l'étape précédente.
                Les étapes qui regroupent plusieurs événements se déplient d'un clic.
              </div>
              <div v-for="(e, i) in funnel" :key="e.key" class="mb-4">
                <div :class="e.detail ? 'etape-cliquable' : ''" @click="toggleDetail(e)">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <span class="font-weight-bold">
                      {{ e.titre }}
                      <i v-if="e.detail" class="fas ml-1 etape-chevron"
                         :class="etapeOuverte === e.key ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </span>
                    <span>
                      <span class="h5 mb-0 mr-2">{{ e.nb }}</span>
                      <b-badge v-if="i > 0" :variant="e.badge">{{ e.tauxPrecedent }}%</b-badge>
                    </span>
                  </div>
                  <div class="piste">
                    <div class="barre" :class="'bg-' + e.color" :style="{ width: e.largeur + '%' }"></div>
                  </div>
                  <div class="text-muted legende">
                    {{ e.tauxTotal }}% des liens envoyés
                    <span v-if="i > 0 && e.perdus > 0"> — {{ e.perdus }} perdus à cette étape</span>
                  </div>
                  <div v-if="e.delai" class="text-muted legende">
                    <i class="far fa-clock mr-1"></i>{{ duree(e.delai.sec) }} après l'étape précédente
                    <span class="legende-discrete">(médiane sur {{ e.delai.n }})</span>
                  </div>
                </div>

                <!-- Détail par type d'événement : d'où vient l'ouverture, quelle
                     demande a été faite. Replié par défaut. -->
                <div v-if="e.detail && etapeOuverte === e.key" class="detail-etape mt-2">
                  <div v-for="d in e.detail" :key="d.event" class="detail-ligne">
                    <div class="d-flex justify-content-between align-items-baseline">
                      <span><i class="mr-2" :class="d.icone"></i>{{ d.libelle }}</span>
                      <span class="text-nowrap">
                        <strong>{{ d.prospects }}</strong>
                        <span class="text-muted legende-discrete"> · {{ d.evenements }} évt{{ d.evenements > 1 ? 's' : '' }}</span>
                      </span>
                    </div>
                    <div class="piste piste-fine">
                      <div class="barre" :class="'bg-' + e.color" :style="{ width: d.part + '%' }"></div>
                    </div>
                  </div>
                  <div v-if="e.detailMultiple" class="text-muted legende mt-2">
                    Un même prospect peut apparaître sur plusieurs lignes : le total dépasse alors
                    les {{ e.nb }} de l'étape.
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- JOUR PAR JOUR -->
          <b-col lg="7">
            <b-card class="card-custom mt-3" title="Jour par jour">
              <div class="text-muted mb-3 sous-titre">
                Liens partis chaque jour, et combien ont fini par être ouverts — même si
                l'ouverture a eu lieu le lendemain. Survolez une barre pour le détail.
              </div>
              <div class="jours-zone">
                <div class="jours">
                  <div v-for="j in parJour" :key="j.jour" class="jour" v-b-tooltip.hover :title="j.tooltip">
                    <div class="jour-barres">
                      <div class="jour-barre bg-primary" :style="{ height: hauteur(j.liens) + '%' }"></div>
                      <div class="jour-barre bg-success" :style="{ height: hauteur(j.ouverts) + '%' }"></div>
                    </div>
                    <div class="jour-taux" :class="j.taux === null ? 'text-muted' : ''">
                      {{ j.taux === null ? '—' : j.taux + '%' }}
                    </div>
                    <div class="jour-label text-muted">{{ j.label }}</div>
                  </div>
                </div>
              </div>
              <div class="mt-3">
                <span class="legende-puce bg-primary"></span> Liens envoyés ce jour-là
                <span class="legende-puce bg-success ml-3"></span> … qui ont été ouverts
              </div>
            </b-card>

            <!-- CONVERSION SELON LE NOMBRE DE VISITES — sous « Jour par jour »,
                 dans la même colonne : le funnel déplié est haut, et seule
                 cette colonne laissait un vide de 500 px à côté de lui. -->
            <b-card class="card-custom mt-3" title="Conversion selon le nombre de visites">
              <div class="text-muted mb-3 sous-titre">
                Une visite = une session : au-delà de {{ SESSION_MIN }} min sans nouvelle ouverture, le retour
                compte comme une nouvelle visite. Un rechargement de page n'en est donc pas une.
              </div>
              <div v-if="visites.message" class="message-cle mb-3">
                <i class="fas fa-lightbulb mr-2"></i>{{ visites.message }}
              </div>
              <table class="table table-sm mb-0 table-visites">
                <thead>
                  <tr>
                    <th></th>
                    <th class="text-right">Prospects</th>
                    <th class="text-right">Infos MàJ</th>
                    <th class="text-right">Devis</th>
                    <th class="colonne-demandes">Demandes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="l in visites.lignes" :key="l.key" :class="l.significatif ? '' : 'non-significatif'">
                    <td class="font-weight-bold text-nowrap">{{ l.libelle }}</td>
                    <td class="text-right">{{ l.prospects }}</td>
                    <td class="text-right">{{ pctOuTiret(l.tauxMaj) }}</td>
                    <td class="text-right">{{ pctOuTiret(l.tauxDevis) }}</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="piste piste-fine flex-grow-1 mt-0 mr-2">
                          <div class="barre bg-danger" :style="{ width: largeurRelative(l.tauxDemande, visites.maxDemande) + '%' }"></div>
                        </div>
                        <span class="text-nowrap valeur-demande">
                          {{ pctOuTiret(l.tauxDemande) }}
                          <span class="text-muted legende-discrete">({{ l.demandes }})</span>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="text-muted legende mt-2">
                Taux calculés sur les prospects de chaque ligne. Lignes grisées : moins de
                {{ MIN_ECHANTILLON }} prospects, taux à lire avec prudence.
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <!-- CARTE DE CHALEUR — pleine largeur : 24 colonnes d'heures s'y lisent
               bien mieux que dans une demi-page. -->
          <b-col lg="12">
            <b-card class="card-custom mt-3" title="Quand ils ouvrent">
              <div class="text-muted mb-3 sous-titre">
                Début de chaque visite, par jour de la semaine et par heure.
                <span v-if="heatmap.pic">
                  Pic : <strong>{{ heatmap.pic.texte }}</strong> ({{ heatmap.pic.n }} visite{{ heatmap.pic.n > 1 ? 's' : '' }}).
                </span>
              </div>
              <div class="heatmap">
                <div class="hm-ligne">
                  <span class="hm-jour"></span>
                  <span v-for="h in 24" :key="'h' + h" class="hm-heure">{{ (h - 1) % 3 === 0 ? (h - 1) + 'h' : '' }}</span>
                </div>
                <div v-for="j in heatmap.jours" :key="j.label" class="hm-ligne">
                  <span class="hm-jour">{{ j.label }}</span>
                  <!-- `title` natif plutôt que v-b-tooltip : 168 cellules, autant
                       ne pas instancier 168 composants de tooltip. -->
                  <span v-for="(n, h) in j.cellules" :key="h" class="hm-cellule"
                        :title="j.long + ' ' + h + 'h : ' + n + ' visite' + (n > 1 ? 's' : '')">
                    <span v-if="n > 0" class="hm-remplissage bg-primary" :style="{ opacity: opaciteCellule(n) }"></span>
                  </span>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-tab>

      <!-- ===================== APPAREILS ===================== -->
      <b-tab>
        <template #title><i class="fas fa-mobile-alt mr-2"></i>Appareils</template>

        <!-- Couverture du suivi : sans elle, un graphe à 99 % « non identifié »
             se lirait comme une information sur les prospects, alors que
             c'en est une sur le tracking. -->
        <b-alert :show="appareils.total > 0 && appareils.couverture < COUVERTURE_MIN" variant="warning" class="mt-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Seules <strong>{{ appareils.couverture }}%</strong> des visites de la période
          ont un appareil identifié ({{ appareils.identifiees }} sur {{ appareils.total }}).
          <span v-if="appareils.premierIdentifie">
            Le premier appareil identifié date du {{ $api.timestampToDate(appareils.premierIdentifie) }} :
            les visites antérieures ne peuvent pas l'être.
          </span>
          Les écarts entre appareils ne seront fiables qu'une fois ce taux élevé.
        </b-alert>

        <b-row>
          <b-col lg="5">
            <b-card class="card-custom mt-3" title="Visites par appareil">
              <div class="text-muted mb-4 sous-titre">
                Appareil utilisé à chaque visite. Les liens envoyés ne comptent pas :
                c'est un événement serveur, sans appareil.
              </div>
              <div v-for="d in appareils.repartition" :key="d.key" class="mb-3" :class="d.key === 'inconnu' ? 'text-muted' : ''">
                <div class="d-flex justify-content-between align-items-baseline">
                  <span><i class="mr-2 icone-appareil" :class="d.icone"></i>{{ d.libelle }}</span>
                  <span><strong>{{ d.n }}</strong> <span class="text-muted legende-discrete">· {{ pctOuTiret(d.part) }}</span></span>
                </div>
                <div class="piste">
                  <div class="barre" :class="d.key === 'inconnu' ? 'bg-secondary' : 'bg-info'"
                       :style="{ width: largeurRelative(d.n, appareils.maxN) + '%' }"></div>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col lg="7">
            <b-card class="card-custom mt-3" title="Parcours par appareil">
              <div class="text-muted mb-3 sous-titre">
                Chaque prospect est rattaché à l'appareil de sa <strong>première</strong> visite,
                pour ne jamais être compté deux fois. Taux calculés sur les prospects de la ligne.
              </div>
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th></th>
                    <th class="text-right">Prospects</th>
                    <th class="text-right">Infos MàJ</th>
                    <th class="text-right">Devis</th>
                    <th class="text-right">Demandes</th>
                    <th class="text-right">Retour</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in appareils.parcours" :key="d.key" :class="d.significatif ? '' : 'non-significatif'">
                    <td class="text-nowrap"><i class="mr-2 icone-appareil" :class="d.icone"></i>{{ d.libelle }}</td>
                    <td class="text-right">{{ d.prospects }}</td>
                    <td class="text-right">{{ pctOuTiret(d.tauxMaj) }}</td>
                    <td class="text-right">{{ pctOuTiret(d.tauxDevis) }}</td>
                    <td class="text-right">{{ pctOuTiret(d.tauxDemande) }}</td>
                    <td class="text-right">{{ pctOuTiret(d.tauxRetour) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="text-muted legende mt-2">
                Lignes grisées : moins de {{ MIN_ECHANTILLON }} prospects, taux non significatifs.
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-tab>

      <!-- ===================== CONSEILLERS ===================== -->
      <b-tab>
        <template #title><i class="fas fa-user-tie mr-2"></i>Conseillers</template>

        <b-card class="card-custom mt-3" title="Par conseiller">
          <div class="text-muted mb-3 sous-titre">
            Toute l'équipe, quel que soit le filtre conseiller : ce tableau sert à comparer.
            Cliquez sur une ligne pour filtrer toute la page sur ce conseiller.
          </div>
          <div class="equipe-ligne mb-3">
            <strong>Moyenne de l'équipe</strong> :
            {{ pctOuTiret(statsEquipe.tauxOuverture) }} d'ouverture ·
            {{ pctOuTiret(statsEquipe.tauxMaj) }} de mise à jour ·
            {{ pctOuTiret(statsEquipe.tauxRetour) }} de retour ·
            ouverture en {{ duree(statsEquipe.delaiOuverture) }}
          </div>
          <b-table
            hover responsive
            :items="parConseiller" :fields="fieldsConseiller"
            sort-by="liens" :sort-desc="true"
            :sort-compare="comparerConseillers"
            :tbody-tr-class="classeLigneConseiller"
            show-empty empty-text="Aucun événement sur la période."
            @row-clicked="choisirConseiller">
            <!-- Valeurs lues sur `data.item`, JAMAIS sur `data.value` : b-table
                 remplace un `null` par une chaîne vide avant de le passer au
                 slot, et un conseiller sans lien affichait alors « % ». -->
            <template #cell(tauxOuverture)="data">
              <CelluleTaux :valeur="data.item.tauxOuverture" :reference="statsEquipe.tauxOuverture"
                           :significatif="data.item.sigLiens" :base="data.item.liens" unite="lien"/>
            </template>
            <template #cell(tauxMaj)="data">
              <CelluleTaux :valeur="data.item.tauxMaj" :reference="statsEquipe.tauxMaj"
                           :significatif="data.item.sigOuverts" :base="data.item.ouverts" unite="prospect ouvert"/>
            </template>
            <template #cell(tauxRetour)="data">
              <CelluleTaux :valeur="data.item.tauxRetour" :reference="statsEquipe.tauxRetour"
                           :significatif="data.item.sigOuverts" :base="data.item.ouverts" unite="prospect ouvert"/>
            </template>
            <template #cell(delaiOuverture)="data">
              <CelluleTaux :valeur="data.item.delaiOuverture" :reference="statsEquipe.delaiOuverture"
                           :significatif="data.item.sigOuverts" :base="data.item.ouverts" unite="prospect ouvert"
                           :texte="duree(data.item.delaiOuverture)" :inverse="true"/>
            </template>
          </b-table>
          <div class="text-muted legende">
            <b-badge variant="success">vert</b-badge> / <b-badge variant="danger">rouge</b-badge> :
            plus de {{ Math.round(ECART_SIGNIFICATIF * 100) }} % au-dessus / en dessous de la moyenne de l'équipe
            (pour le délai, plus court = mieux). Grisé : moins de {{ MIN_ECHANTILLON }} prospects dans la base
            du taux ; ces lignes restent en bas quand on trie sur un taux.
          </div>
        </b-card>
      </b-tab>
    </b-tabs>

  </div>
</template>

<script>
/* =============================================================================
 * Statistiques du parcours client.
 *
 * DEUX appels : GET /client/parcours?start=…&end=… pour la période demandée,
 * et le même sur la période précédente de même durée, pour les écarts des KPI.
 * Chacun renvoie la liste BRUTE des événements ; tout le reste est calculé ici,
 * en `computed`. Le volume reste faible (quelques milliers de lignes par
 * semaine) et le filtre conseiller s'applique sans retourner voir le serveur.
 *
 * Forme d'un événement :
 *   { date_create: <timestamp s>, event: <string>, device: <string|null>,
 *     client: { id, nom, prenom, user_id } }
 *
 * `device` vaut null sur les événements émis par le SERVEUR (`lien_envoyee`),
 * et 'inconnu' sur les événements navigateur antérieurs au suivi d'appareil.
 * Les deux ne signifient pas la même chose et ne sont pas confondus ici.
 *
 * La page raisonne en PROSPECTS, jamais en événements : un prospect qui recharge
 * cinq fois ne doit pas peser cinq fois plus qu'un autre. Et en VISITES
 * (sessions), jamais en ouvertures brutes, pour la même raison.
 * ============================================================================= */
import Input from '@/components/InputForm';

/* Noms d'événements.
 *
 * `ouverutre` est une faute de frappe côté back (pour « ouverture ») et elle
 * porte la majorité des données. On teste donc le PRÉFIXE `ouver`, qui couvre
 * d'un coup `ouverutre`, ses variantes de canal `ouverutre_mail|_sms|_wa`, et
 * `ouverture` si la faute venait à être corrigée — sans rien casser ici. */
const EV_LIEN  = 'lien_envoyee';
const EV_MAJ   = 'maj_info';
const EV_DEVIS = 'envoi_devis';
// Émis par le parcours la 1re fois que des offres sont à l'écran (une fois par
// chargement de page). Depuis l'arrivée directe sur les offres, c'est lui qui
// dit si le prospect a vu ses tarifs — `maj_info` ne le dit plus.
const EV_TARIFS = 'affichage_tarifs';
const estOuverture = ev => typeof ev === 'string' && ev.indexOf('ouver') === 0;
const estCta       = ev => typeof ev === 'string' && ev.indexOf('cta_') === 0;

/* Une visite = une session. Au-delà de ce délai sans nouvelle ouverture, le
 * retour compte comme une nouvelle visite. 30 min est la convention des outils
 * d'analyse d'audience. Mesuré sur la semaine du 16 au 22/09 : sans ce
 * regroupement, les rechargements de page faisaient passer 45 prospects pour
 * des « revenants » alors qu'ils n'étaient jamais partis — et gonflaient
 * artificiellement la conversion des visites multiples. */
const SESSION_MIN = 30;
const SESSION_S = SESSION_MIN * 60;

/* En dessous de ce nombre de prospects, un taux est affiché grisé : 3 liens
 * envoyés et 3 ouverts donnent « 100 % », ce qui classerait en tête un
 * conseiller qui n'a presque rien fait. */
const MIN_ECHANTILLON = 10;

/* Écart relatif à la moyenne de l'équipe à partir duquel un taux est coloré. */
const ECART_SIGNIFICATIF = 0.2;

/* Sous ce pourcentage de visites à appareil identifié, l'onglet Appareils
 * affiche un avertissement : ses écarts parlent du tracking, pas des gens. */
const COUVERTURE_MIN = 50;

const APPAREILS = [
  { key: 'smartphone', libelle: 'Smartphone',    icone: 'fas fa-mobile-alt' },
  { key: 'pc',         libelle: 'Ordinateur',    icone: 'fas fa-desktop' },
  { key: 'tablette',   libelle: 'Tablette',      icone: 'fas fa-tablet-alt' },
  { key: 'inconnu',    libelle: 'Non identifié', icone: 'fas fa-question-circle' }
];
const APPAREILS_CONNUS = ['smartphone', 'pc', 'tablette'];

const JOURS = [
  { court: 'Lun', long: 'Lundi' }, { court: 'Mar', long: 'Mardi' },
  { court: 'Mer', long: 'Mercredi' }, { court: 'Jeu', long: 'Jeudi' },
  { court: 'Ven', long: 'Vendredi' }, { court: 'Sam', long: 'Samedi' },
  { court: 'Dim', long: 'Dimanche' }
];

/* Libellé et icône de chaque type d'événement, pour le détail d'une étape.
 *
 * Volontairement incomplet : tout événement absent de cette table est affiché
 * sous son nom brut plutôt qu'ignoré. Si le back en ajoute un — un nouveau
 * canal d'ouverture, une nouvelle demande — il apparaît tout seul. */
const LIBELLES = {
  'ouverutre':                { libelle: 'Ouverture directe',       icone: 'fas fa-link text-secondary' },
  'ouverture':                { libelle: 'Ouverture directe',       icone: 'fas fa-link text-secondary' },
  'ouverutre_mail':           { libelle: 'Depuis un mail',          icone: 'fas fa-envelope text-primary' },
  'ouverutre_sms':            { libelle: 'Depuis un SMS',           icone: 'fas fa-comment-dots text-info' },
  'ouverutre_wa':             { libelle: 'Depuis WhatsApp',         icone: 'fab fa-whatsapp text-success' },
  'cta_demande_rappel':       { libelle: 'Demande de rappel',       icone: 'fas fa-phone text-primary' },
  'cta_demande_souscription': { libelle: 'Demande de souscription', icone: 'fas fa-file-signature text-success' },
  'cta_client_interesse':     { libelle: 'Client intéressé',        icone: 'fas fa-star text-warning' }
};

/* ---------------------------------------------------------------------------
 * Fonctions pures. Hors du composant parce qu'elles servent deux fois — pour
 * la période demandée et pour la précédente — et qu'elles se testent seules.
 * ------------------------------------------------------------------------- */

const arrondi = v => Math.round(v * 10) / 10;
const pct = (n, total) => (total > 0 ? arrondi(100 * n / total) : null);

function mediane(valeurs) {
  if (valeurs.length === 0) return null;
  const v = valeurs.slice().sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : Math.round((v[m - 1] + v[m]) / 2);
}

/** Tout ce qui n'est pas un appareil connu est « inconnu » — y compris null. */
function normaliserAppareil(device) {
  return APPAREILS_CONNUS.indexOf(device) !== -1 ? device : 'inconnu';
}

/**
 * Un objet par prospect distinct, qui résume tout ce qu'il a fait.
 * C'est la brique de TOUS les calculs de la page.
 */
function construireProspects(events) {
  const map = new Map();
  events.forEach(e => {
    const c = e && e.client;
    // Un événement sans nom (`event: ""`, prévu par l'API) n'appartient à
    // aucune étape : il ne doit pas non plus créer de prospect fantôme.
    if (!c || c.id === undefined || c.id === null || !e.event) return;
    let p = map.get(c.id);
    if (p === undefined) {
      p = {
        id: c.id,
        nom: [c.nom, c.prenom].filter(Boolean).join(' '),
        user_id: c.user_id,
        // Horodatage de la PREMIÈRE occurrence de chaque étape.
        lienAt: null, ouvAt: null, majAt: null, tarifsAt: null, devisAt: null, ctaAt: null,
        // Nombre de chaque type d'événement : sert au détail d'une étape.
        parEvent: {},
        ouvertures: []
      };
      map.set(c.id, p);
    }
    p.parEvent[e.event] = (p.parEvent[e.event] || 0) + 1;
    const t = e.date_create;
    const premier = cle => { if (p[cle] === null || t < p[cle]) p[cle] = t; };
    if (e.event === EV_LIEN) premier('lienAt');
    else if (estOuverture(e.event)) {
      premier('ouvAt');
      p.ouvertures.push({ t, device: normaliserAppareil(e.device) });
    }
    else if (e.event === EV_MAJ) premier('majAt');
    else if (e.event === EV_TARIFS) premier('tarifsAt');
    else if (e.event === EV_DEVIS) premier('devisAt');
    else if (estCta(e.event)) premier('ctaAt');
  });

  const prospects = Array.from(map.values());
  prospects.forEach(p => {
    // Regroupement des ouvertures en visites. Une visite prend l'appareil de
    // la première de ses ouvertures qui en a un : les ouvertures d'une même
    // session peuvent mélanger « inconnu » et un appareil réel.
    p.ouvertures.sort((a, b) => a.t - b.t);
    p.visitesListe = [];
    let courante = null;
    let derniere = null;
    p.ouvertures.forEach(o => {
      if (courante === null || o.t - derniere > SESSION_S) {
        courante = { t: o.t, device: 'inconnu' };
        p.visitesListe.push(courante);
      }
      if (courante.device === 'inconnu' && o.device !== 'inconnu') courante.device = o.device;
      derniere = o.t;
    });
    p.visites = p.visitesListe.length;
    p.appareil = p.visites > 0 ? p.visitesListe[0].device : null;
  });
  return prospects;
}

/**
 * Les indicateurs d'un ensemble de prospects. Sert pour la page entière, pour
 * la période précédente, et pour chaque conseiller.
 *
 * Deux dénominateurs, et ce n'est pas un détail :
 *   - le taux d'ouverture se rapporte aux LIENS ENVOYÉS, et ne compte que les
 *     ouvertures de ces liens — il ne peut donc pas dépasser 100 % ;
 *   - tous les taux suivants se rapportent aux prospects OUVERTS : ce qu'ils
 *     font une fois dans le parcours, indépendamment de l'envoi.
 */
function statistiques(prospects) {
  const avecLien = prospects.filter(p => p.lienAt !== null);
  const ouverts = prospects.filter(p => p.ouvAt !== null);
  const ouvertsAvecLien = avecLien.filter(p => p.ouvAt !== null);
  const compte = (liste, champ) => liste.filter(p => p[champ] !== null).length;
  const revenus = ouverts.filter(p => p.visites >= 2).length;

  return {
    prospects: prospects.length,
    liens: avecLien.length,
    ouverts: ouverts.length,
    ouvertsAvecLien: ouvertsAvecLien.length,
    maj: compte(prospects, 'majAt'),
    tarifs: compte(prospects, 'tarifsAt'),
    devis: compte(prospects, 'devisAt'),
    devisOuverts: compte(ouverts, 'devisAt'),
    cta: compte(prospects, 'ctaAt'),
    revenus,
    ouvertSansLien: ouverts.length - ouvertsAvecLien.length,
    tauxOuverture: pct(ouvertsAvecLien.length, avecLien.length),
    tauxMaj:       pct(compte(ouverts, 'majAt'), ouverts.length),
    tauxTarifs:    pct(compte(ouverts, 'tarifsAt'), ouverts.length),
    tauxDevis:     pct(compte(ouverts, 'devisAt'), ouverts.length),
    tauxDemande:   pct(compte(ouverts, 'ctaAt'), ouverts.length),
    tauxRetour:    pct(revenus, ouverts.length),
    // Médiane et non moyenne : quelques prospects ouvrent au bout de
    // plusieurs jours et tireraient une moyenne hors de tout rapport avec le
    // vécu.
    delaiOuverture: mediane(ouvertsAvecLien
      .filter(p => p.ouvAt >= p.lienAt)
      .map(p => p.ouvAt - p.lienAt))
  };
}

/** Taux sur un sous-ensemble : MàJ, devis, demandes et retour, sur les ouverts. */
function tauxSur(liste) {
  const ouverts = liste.filter(p => p.ouvAt !== null);
  return {
    tauxMaj:     pct(ouverts.filter(p => p.majAt !== null).length, ouverts.length),
    tauxDevis:   pct(ouverts.filter(p => p.devisAt !== null).length, ouverts.length),
    tauxDemande: pct(ouverts.filter(p => p.ctaAt !== null).length, ouverts.length),
    tauxRetour:  pct(ouverts.filter(p => p.visites >= 2).length, ouverts.length),
    demandes:    ouverts.filter(p => p.ctaAt !== null).length
  };
}

/**
 * Classe de couleur d'un taux comparé à la moyenne de l'équipe.
 * `inverse` : pour un délai, plus court est meilleur.
 */
function appreciation(valeur, reference, significatif, inverse) {
  // '' traité comme absent : c'est ce que b-table fait d'un null (voir le
  // template). Double protection, pour qu'un futur `data.value` ne réintroduise
  // pas le « % » orphelin.
  if (valeur === null || valeur === undefined || valeur === '') return 'vide';
  if (!significatif) return 'non-significatif';
  if (reference === null || reference === undefined || reference === 0) return 'neutre';
  let ecart = (valeur - reference) / reference;
  if (inverse) ecart = -ecart;
  if (ecart > ECART_SIGNIFICATIF) return 'success';
  if (ecart < -ECART_SIGNIFICATIF) return 'danger';
  return 'neutre';
}

/* Cellule de taux du tableau des conseillers : badge coloré selon l'écart à
 * l'équipe, ou valeur grisée quand l'échantillon est trop petit. */
const CelluleTaux = {
  name: 'CelluleTaux',
  props: {
    valeur: { default: null },
    reference: { default: null },
    significatif: { type: Boolean, default: true },
    base: { type: Number, default: 0 },
    unite: { type: String, default: 'prospect' },
    texte: { type: String, default: null },
    inverse: { type: Boolean, default: false }
  },
  computed: {
    classe() {
      return appreciation(this.valeur, this.reference, this.significatif, this.inverse);
    },
    affichage() {
      if (this.texte !== null) return this.texte;
      return this.valeur === null ? '—' : this.valeur + '%';
    },
    infobulle() {
      if (this.classe === 'non-significatif') {
        return 'Calculé sur ' + this.base + ' ' + this.unite + (this.base > 1 ? 's' : '') + ' : non significatif';
      }
      return null;
    }
  },
  render(h) {
    if (this.classe === 'vide') return h('span', { class: 'text-muted' }, '—');
    if (this.classe === 'non-significatif') {
      return h('span', { class: 'text-muted taux-grise', attrs: { title: this.infobulle } }, this.affichage);
    }
    if (this.classe === 'neutre') return h('span', this.affichage);
    return h('b-badge', { props: { variant: this.classe } }, this.affichage);
  }
};

export default {
  name: "ParcoursStats",
  components: { Input, CelluleTaux },
  data() {
    return {
      load: false,
      dateError: null,
      SESSION_MIN,
      MIN_ECHANTILLON,
      ECART_SIGNIFICATIF,
      COUVERTURE_MIN,
      events: [],
      // null = période précédente non chargée (erreur) : pas de comparaison.
      eventsPrec: null,
      periodePrec: null,
      onglet: 0,
      // Clé de l'étape du funnel dépliée, ou null. Une seule à la fois.
      etapeOuverte: null,
      filters: {
        start: this.$api.moment().subtract(6, 'days').format('YYYY-MM-DD'),
        end:   this.$api.moment().format('YYYY-MM-DD'),
        user_id: []
      },
      presets: [
        { key: '7j',   titre: '7 derniers jours',  start: () => this.$api.moment().subtract(6, 'days'),  end: () => this.$api.moment() },
        { key: '30j',  titre: '30 derniers jours', start: () => this.$api.moment().subtract(29, 'days'), end: () => this.$api.moment() },
        { key: 'mois', titre: 'Mois en cours',     start: () => this.$api.moment().startOf('month'),     end: () => this.$api.moment() },
        { key: 'prec', titre: 'Mois précédent',    start: () => this.$api.moment().subtract(1, 'month').startOf('month'), end: () => this.$api.moment().subtract(1, 'month').endOf('month') }
      ],
      fieldsConseiller: [
        { key: 'conseiller',     label: 'Conseiller',     sortable: true },
        { key: 'liens',          label: 'Liens',          sortable: true, class: 'text-right' },
        { key: 'tauxOuverture',  label: '% ouverture',    sortable: true, class: 'text-right' },
        { key: 'tauxMaj',        label: '% infos MàJ',    sortable: true, class: 'text-right' },
        { key: 'devis',          label: 'Devis',          sortable: true, class: 'text-right' },
        { key: 'cta',            label: 'Demandes',       sortable: true, class: 'text-right' },
        { key: 'tauxRetour',     label: '% retour',       sortable: true, class: 'text-right' },
        { key: 'delaiOuverture', label: 'Délai ouverture', sortable: true, class: 'text-right' }
      ]
    }
  },
  computed: {
    /** Toute l'équipe, sur la période : base du tableau des conseillers. */
    prospectsEquipe() {
      return construireProspects(this.events);
    },
    /**
     * Les prospects après filtre conseiller. On filtre les prospects et non
     * les événements : même résultat, une seule construction.
     */
    prospects() {
      return this.filtrerConseiller(this.prospectsEquipe);
    },
    prospectsPrec() {
      return this.eventsPrec === null ? null : this.filtrerConseiller(construireProspects(this.eventsPrec));
    },
    stats() {
      return statistiques(this.prospects);
    },
    statsPrec() {
      return this.prospectsPrec === null ? null : statistiques(this.prospectsPrec);
    },
    statsEquipe() {
      return statistiques(this.prospectsEquipe);
    },
    conseillersChoisis() {
      return (this.filters.user_id || []).map(id => this.nomUser(id));
    },

    kpis() {
      const s = this.stats;
      const p = this.statsPrec;
      const prec = cle => (p === null ? null : p[cle]);
      const nombre = v => String(v);
      const taux = v => v + '%';
      return [
        this.kpi("Liens envoyés",       s.liens,          nombre,     prec('liens'),          'volume', 'primary',
          s.prospects + ' prospects touchés'),
        this.kpi("Taux d'ouverture",    s.tauxOuverture,  taux,       prec('tauxOuverture'),  'taux',   'info',
          s.ouvertsAvecLien + ' liens ouverts'),
        this.kpi("Infos mises à jour",  s.tauxMaj,        taux,       prec('tauxMaj'),        'taux',   'warning',
          'des parcours ouverts'),
        this.kpi("Tarifs affichés",     s.tauxTarifs,     taux,       prec('tauxTarifs'),     'taux',   'primary',
          s.tarifs + ' prospects · des parcours ouverts'),
        this.kpi("Devis envoyés",       s.devis,          nombre,     prec('devis'),          'volume', 'success',
          // `devis` compte aussi les prospects qui n'ont jamais ouvert : le
          // taux, lui, ne porte que sur les ouverts — on montre les deux bases.
          'dont ' + s.devisOuverts + ' après ouverture · ' + this.pctOuTiret(s.tauxDevis) + ' des ouverts'),
        this.kpi("Demandes",            s.cta,            nombre,     prec('cta'),            'volume', 'danger',
          'rappel, intérêt, souscription'),
        this.kpi("Taux de retour",      s.tauxRetour,     taux,       prec('tauxRetour'),     'taux',   'dark',
          s.revenus + ' revenus au moins 2 fois'),
        this.kpi("Délai d'ouverture",   s.delaiOuverture, this.duree, prec('delaiOuverture'), 'delai',  'secondary',
          'médiane après envoi')
      ];
    },

    /**
     * Le funnel, en prospects distincts.
     *
     * Chaque étage compte les prospects qui l'ont ATTEINT, sans exiger qu'ils
     * aient franchi le précédent : si un prospect ouvre sans lien tracé, il
     * apparaît en « ouvert ». C'est le seul moyen de voir les trous de
     * tracking au lieu de les masquer (cf. `ouvertSansLien`).
     */
    funnel() {
      const s = this.stats;
      const brut = [
        { key: 'liens',   titre: 'Liens envoyés',             nb: s.liens,   color: 'primary', champ: 'lienAt',  filtre: ev => ev === EV_LIEN },
        { key: 'ouverts', titre: 'Parcours ouvert',           nb: s.ouverts, color: 'info',    champ: 'ouvAt',   filtre: estOuverture },
        { key: 'maj',     titre: 'Informations mises à jour', nb: s.maj,     color: 'warning', champ: 'majAt',   filtre: ev => ev === EV_MAJ },
        { key: 'tarifs',  titre: 'Tarifs affichés',           nb: s.tarifs,  color: 'primary', champ: 'tarifsAt', filtre: ev => ev === EV_TARIFS },
        { key: 'devis',   titre: 'Devis envoyé',              nb: s.devis,   color: 'success', champ: 'devisAt', filtre: ev => ev === EV_DEVIS },
        { key: 'cta',     titre: 'Rappel / intérêt / souscription', nb: s.cta, color: 'danger', champ: 'ctaAt', filtre: estCta }
      ];
      const base = s.liens > 0 ? s.liens : (brut.reduce((m, e) => Math.max(m, e.nb), 0) || 1);
      return brut.map((e, i) => {
        const prec = i > 0 ? brut[i - 1] : null;
        const tauxPrecedent = (prec !== null && prec.nb > 0) ? arrondi(100 * e.nb / prec.nb) : 0;
        const detail = this.detailEtape(e);
        return Object.assign({}, e, {
          // Plancher à 1,5% : les dernières étapes pèsent 1 à 2% et leur barre
          // disparaîtrait, alors que ce sont elles qu'on vient regarder.
          largeur: e.nb === 0 ? 0 : Math.min(100, Math.max(1.5, arrondi(100 * e.nb / base))),
          tauxTotal: arrondi(100 * e.nb / base),
          tauxPrecedent,
          perdus: (prec !== null && prec.nb > e.nb) ? prec.nb - e.nb : 0,
          badge: i === 0 ? 'primary' : this.variantTaux(tauxPrecedent, [25, 60]),
          // Temps écoulé depuis l'étape précédente, chez ceux qui ont franchi
          // les deux : dit si le prospect décide sur le moment ou revient.
          delai: prec === null ? null : this.delaiEntre(prec.champ, e.champ),
          // `detail` n'existe que si l'étape regroupe VRAIMENT plusieurs types
          // d'événements : sinon on promettrait une information absente.
          detail: detail.length > 1 ? detail : null,
          detailMultiple: detail.reduce((t, d) => t + d.prospects, 0) > e.nb
        });
      });
    },

    /**
     * Un point par jour de la période — y compris les jours sans envoi.
     *
     * ⚠️ Chaque jour est une COHORTE D'ENVOI : l'ouverture est rattachée au
     * jour où le lien est parti, même si le prospect a cliqué le lendemain.
     * Compter les ouvertures à leur propre date donnait « 314 % d'ouverture »
     * un samedi à 7 envois et 22 ouvertures venues de la veille.
     */
    parJour() {
      const jours = new Map();
      const debut = this.$api.moment(this.filters.start, 'YYYY-MM-DD');
      const fin   = this.$api.moment(this.filters.end, 'YYYY-MM-DD');
      if (debut.isValid() && fin.isValid() && !debut.isAfter(fin)) {
        const curseur = debut.clone();
        let garde = 0; // une période absurde ne doit pas bloquer la page
        while (!curseur.isAfter(fin, 'day') && garde < 400) {
          jours.set(curseur.format('YYYY-MM-DD'), { liens: 0, ouverts: 0 });
          curseur.add(1, 'day');
          garde += 1;
        }
      }
      this.prospects.forEach(p => {
        if (p.lienAt === null) return;
        const k = this.$api.moment(p.lienAt * 1000).format('YYYY-MM-DD');
        let j = jours.get(k);
        if (j === undefined) { j = { liens: 0, ouverts: 0 }; jours.set(k, j); }
        j.liens += 1;
        if (p.ouvAt !== null) j.ouverts += 1;
      });
      return Array.from(jours.keys()).sort().map(k => {
        const j = jours.get(k);
        const taux = pct(j.ouverts, j.liens);
        return {
          jour: k,
          label: this.$api.moment(k, 'YYYY-MM-DD').format('DD/MM'),
          liens: j.liens,
          ouverts: j.ouverts,
          taux,
          tooltip: this.$api.moment(k, 'YYYY-MM-DD').format('dddd DD/MM')
            + ' — ' + j.liens + ' lien(s) envoyé(s), ' + j.ouverts + ' ouvert(s)'
            + (taux === null ? '' : ' (' + taux + '%)')
        };
      });
    },
    maxJour() {
      return this.parJour.reduce((m, j) => Math.max(m, j.liens, j.ouverts), 0);
    },

    /**
     * Conversion selon le nombre de visites : répond à « est-ce que ceux qui
     * reviennent convertissent mieux ? ». Sur la semaine du 16 au 22/09 : 1,5 %
     * de demandes après une visite, 11 à 14 % après deux ou plus.
     */
    visites() {
      const ouverts = this.prospects.filter(p => p.visites > 0);
      const groupes = [
        { key: '1', libelle: '1 visite',       test: v => v === 1 },
        { key: '2', libelle: '2 visites',      test: v => v === 2 },
        { key: '3', libelle: '3 visites et +', test: v => v >= 3 }
      ];
      const lignes = groupes.map(g => {
        const liste = ouverts.filter(p => g.test(p.visites));
        return Object.assign({ key: g.key, libelle: g.libelle, prospects: liste.length,
          significatif: liste.length >= MIN_ECHANTILLON }, tauxSur(liste));
      });
      const maxDemande = lignes.reduce((m, l) => Math.max(m, l.tauxDemande || 0), 0);
      return { lignes, maxDemande, message: this.messageVisites(ouverts, lignes[0]) };
    },

    /**
     * Carte de chaleur jour × heure, sur le DÉBUT des visites : une visite de
     * vingt minutes ne doit pas colorer vingt minutes de rechargements.
     */
    heatmap() {
      const grille = JOURS.map(() => new Array(24).fill(0));
      this.prospects.forEach(p => p.visitesListe.forEach(v => {
        const m = this.$api.moment(v.t * 1000);
        grille[m.isoWeekday() - 1][m.hour()] += 1;
      }));
      let max = 0;
      let pic = null;
      grille.forEach((ligne, j) => ligne.forEach((n, h) => {
        if (n > max) { max = n; pic = { texte: JOURS[j].long.toLowerCase() + ' ' + h + 'h', n }; }
      }));
      return {
        jours: JOURS.map((j, i) => ({ label: j.court, long: j.long, cellules: grille[i] })),
        max,
        pic
      };
    },

    appareils() {
      const visites = [];
      this.prospects.forEach(p => p.visitesListe.forEach(v => visites.push(v)));
      const total = visites.length;
      const identifiees = visites.filter(v => v.device !== 'inconnu').length;
      const repartition = APPAREILS.map(a => {
        const n = visites.filter(v => v.device === a.key).length;
        return Object.assign({}, a, { n, part: pct(n, total) });
      });
      // Rattachement à l'appareil de la PREMIÈRE visite : un prospect qui
      // découvre sur son téléphone et revient sur son PC n'est compté qu'une
      // fois, du côté où il a découvert le parcours.
      const parcours = APPAREILS.map(a => {
        const liste = this.prospects.filter(p => p.appareil === a.key);
        return Object.assign({}, a, { prospects: liste.length,
          significatif: liste.length >= MIN_ECHANTILLON }, tauxSur(liste));
      });
      let premierIdentifie = null;
      this.prospects.forEach(p => p.ouvertures.forEach(o => {
        if (o.device !== 'inconnu' && (premierIdentifie === null || o.t < premierIdentifie)) premierIdentifie = o.t;
      }));
      return {
        total,
        identifiees,
        couverture: pct(identifiees, total),
        repartition,
        maxN: repartition.reduce((m, r) => Math.max(m, r.n), 0),
        parcours,
        premierIdentifie
      };
    },

    /**
     * Toute l'équipe, quel que soit le filtre conseiller : le tableau sert à
     * comparer, et une moyenne d'équipe calculée sur un seul conseiller ne
     * serait que lui-même.
     */
    parConseiller() {
      const groupes = new Map();
      this.prospectsEquipe.forEach(p => {
        if (!groupes.has(p.user_id)) groupes.set(p.user_id, []);
        groupes.get(p.user_id).push(p);
      });
      return Array.from(groupes.entries()).map(([userId, liste]) => {
        const s = statistiques(liste);
        return {
          user_id: userId,
          conseiller: this.nomUser(userId),
          liens: s.liens,
          ouverts: s.ouverts,
          devis: s.devis,
          cta: s.cta,
          tauxOuverture: s.tauxOuverture,
          tauxMaj: s.tauxMaj,
          tauxRetour: s.tauxRetour,
          delaiOuverture: s.delaiOuverture,
          sigLiens: s.liens >= MIN_ECHANTILLON,
          sigOuverts: s.ouverts >= MIN_ECHANTILLON
        };
      });
    }
  },
  methods: {
    filtrerConseiller(prospects) {
      const ids = this.filters.user_id;
      if (!ids || ids.length === 0) return prospects;
      return prospects.filter(p => ids.indexOf(p.user_id) !== -1);
    },

    /**
     * Un KPI et son écart à la période précédente.
     * nature : 'volume' (écart en %), 'taux' (écart en points),
     *          'delai' (écart en %, et plus court = mieux).
     */
    kpi(titre, valeur, format, precedente, nature, color, detail) {
      return {
        titre,
        valeur: valeur === null ? '—' : format(valeur),
        detail,
        color,
        delta: this.delta(valeur, precedente, nature, format)
      };
    },
    delta(valeur, precedente, nature, format) {
      if (this.statsPrec === null || valeur === null || precedente === null || precedente === undefined) return null;
      let ecart;
      let texte;
      if (nature === 'taux') {
        ecart = arrondi(valeur - precedente);
        texte = (ecart > 0 ? '+' : '') + ecart + ' pts';
      } else {
        // Pas de pourcentage d'évolution depuis zéro : « +∞ % » n'apprend rien.
        if (precedente === 0) return valeur === 0 ? this.deltaNeutre(format(0)) : null;
        ecart = Math.round(100 * (valeur - precedente) / precedente);
        texte = (ecart > 0 ? '+' : '') + ecart + ' %';
      }
      if (ecart === 0) return this.deltaNeutre(format(precedente));
      const meilleur = nature === 'delai' ? ecart < 0 : ecart > 0;
      return {
        texte,
        classe: meilleur ? 'text-success' : 'text-danger',
        icone: ecart > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down',
        tooltip: 'Période précédente : ' + format(precedente)
      };
    },
    deltaNeutre(precedente) {
      return { texte: 'stable', classe: 'text-muted', icone: 'fas fa-equals', tooltip: 'Période précédente : ' + precedente };
    },

    /**
     * Décompose une étape du funnel par TYPE d'événement : d'où vient
     * l'ouverture, quelle demande a été faite. Deux compteurs par ligne :
     * `prospects` (personnes distinctes, comparable au total de l'étape) et
     * `evenements` (réouvertures comprises). Les types sont découverts dans
     * les données, jamais codés en dur.
     */
    detailEtape(etape) {
      const compte = {};
      this.prospects.forEach(p => {
        Object.keys(p.parEvent).forEach(ev => {
          if (!etape.filtre(ev)) return;
          if (compte[ev] === undefined) compte[ev] = { prospects: 0, evenements: 0 };
          compte[ev].prospects += 1;
          compte[ev].evenements += p.parEvent[ev];
        });
      });
      return Object.keys(compte)
        .map(ev => {
          const connu = LIBELLES[ev];
          return {
            event: ev,
            libelle: connu ? connu.libelle : ev,
            icone: connu ? connu.icone : 'fas fa-circle text-muted',
            prospects: compte[ev].prospects,
            evenements: compte[ev].evenements,
            // Plancher à 1,5% : un canal à 0,3% est justement celui qu'on
            // est venu chercher.
            part: etape.nb > 0
              ? Math.min(100, Math.max(1.5, arrondi(100 * compte[ev].prospects / etape.nb)))
              : 0
          };
        })
        .sort((a, b) => b.prospects - a.prospects);
    },
    toggleDetail(etape) {
      if (!etape.detail) return;
      this.etapeOuverte = this.etapeOuverte === etape.key ? null : etape.key;
    },
    /** Délai médian entre deux étapes, chez les prospects qui ont franchi les deux. */
    delaiEntre(champA, champB) {
      const delais = this.prospects
        .filter(p => p[champA] !== null && p[champB] !== null && p[champB] >= p[champA])
        .map(p => p[champB] - p[champA]);
      return delais.length === 0 ? null : { sec: mediane(delais), n: delais.length };
    },

    /**
     * La phrase qui résume le tableau des visites. Seulement quand il y a de
     * quoi l'écrire : sous 3 demandes, un ratio ne veut rien dire.
     */
    messageVisites(ouverts, uneVisite) {
      const revenus = ouverts.filter(p => p.visites >= 2);
      const demandesRevenus = revenus.filter(p => p.ctaAt !== null).length;
      const total = uneVisite.demandes + demandesRevenus;
      if (total < 3 || uneVisite.prospects === 0 || revenus.length === 0) return null;
      const tauxRevenus = pct(demandesRevenus, revenus.length);
      if (uneVisite.tauxDemande === 0) {
        return demandesRevenus > 0
          ? 'Toutes les demandes (' + demandesRevenus + ') viennent de prospects revenus au moins deux fois.'
          : null;
      }
      const ratio = tauxRevenus / uneVisite.tauxDemande;
      if (ratio >= 1.5) {
        // Même séparateur décimal que tous les taux de la page (le point) :
        // « 7,9 fois » à côté de « 11.8% » se lisait comme une coquille.
        return 'Les prospects revenus au moins deux fois font une demande '
          + arrondi(ratio) + ' fois plus souvent ('
          + tauxRevenus + '% contre ' + uneVisite.tauxDemande + '% après une seule visite).';
      }
      if (ratio <= 1 / 1.5) {
        return 'Les prospects revenus convertissent MOINS que ceux d\'une seule visite ('
          + tauxRevenus + '% contre ' + uneVisite.tauxDemande + '%).';
      }
      return null;
    },

    /**
     * Tri du tableau des conseillers : les taux non significatifs restent en
     * BAS dans les deux sens. Sans cela, trier « % ouverture » décroissant
     * mettait en tête un conseiller à 100 % sur 3 envois.
     *
     * BootstrapVue multiplie le résultat par -1 en tri décroissant
     * (`result * (sortDesc ? -1 : 1)`, vérifié en 2.23) : pour qu'un élément
     * reste en bas quel que soit le sens, on renvoie le signe déjà inversé.
     */
    comparerConseillers(a, b, cle, sortDesc) {
      const base = { tauxOuverture: 'sigLiens', tauxMaj: 'sigOuverts', tauxRetour: 'sigOuverts', delaiOuverture: 'sigOuverts' }[cle];
      if (!base) return null; // tri par défaut
      const valide = x => x[base] && x[cle] !== null && x[cle] !== undefined;
      const va = valide(a);
      const vb = valide(b);
      const enBas = sortDesc ? -1 : 1;
      if (va && !vb) return -enBas;
      if (!va && vb) return enBas;
      if (!va && !vb) return 0;
      return a[cle] < b[cle] ? -1 : (a[cle] > b[cle] ? 1 : 0);
    },
    classeLigneConseiller(item, type) {
      if (type !== 'row' || !item) return '';
      const choisi = (this.filters.user_id || []).indexOf(item.user_id) !== -1;
      return 'ligne-cliquable' + (choisi ? ' ligne-choisie' : '');
    },
    /** Clic sur un conseiller : toute la page se filtre sur lui. */
    choisirConseiller(item) {
      this.filters.user_id = [item.user_id];
      this.onglet = 0;
    },

    /** Hauteur d'une barre, en % du plus grand jour de la période. */
    hauteur(valeur) {
      if (this.maxJour === 0 || valeur === 0) return 0;
      // 2% de plancher : une valeur faible mais NON NULLE doit rester visible.
      return Math.max(2, Math.round(100 * valeur / this.maxJour));
    },
    largeurRelative(valeur, max) {
      if (!valeur || !max) return 0;
      return Math.max(1.5, Math.round(100 * valeur / max));
    },
    opaciteCellule(n) {
      // De 0,15 à 1 : une case à une seule visite doit rester visible.
      return this.heatmap.max === 0 ? 0 : (0.15 + 0.85 * n / this.heatmap.max).toFixed(2);
    },
    pctOuTiret(v) {
      return v === null || v === undefined ? '—' : v + '%';
    },
    /**
     * Durée en secondes → « 35 min », « 4 h », « 3 j ».
     * Toujours arrondi vers le BAS : « 1 j » se lit « plus d'un jour ».
     */
    duree(sec) {
      if (sec === null || sec === undefined) return '—';
      if (sec < 3600) return Math.max(1, Math.floor(sec / 60)) + ' min';
      if (sec < 86400) return Math.floor(sec / 3600) + ' h';
      return Math.floor(sec / 86400) + ' j';
    },
    variantTaux(valeur, [bas, haut]) {
      if (valeur < bas) return 'danger';
      if (valeur >= haut) return 'success';
      return 'warning';
    },
    nomUser(id) {
      const u = this.$api.p('User', id);
      return u.titre || ('Utilisateur #' + id);
    },
    formatJour(iso) {
      return this.$api.moment(iso, 'YYYY-MM-DD').format('DD/MM/YYYY');
    },
    isPreset(p) {
      return this.filters.start === p.start().format('YYYY-MM-DD')
          && this.filters.end === p.end().format('YYYY-MM-DD');
    },
    applyPreset(p) {
      this.filters.start = p.start().format('YYYY-MM-DD');
      this.filters.end = p.end().format('YYYY-MM-DD');
      this.init();
    },

    /** Selon les routes, l'API renvoie { status, data } ou le tableau directement. */
    extraire(res) {
      if (Array.isArray(res)) return res;
      if (res && res.status === true) return Array.isArray(res.data) ? res.data : [];
      return null;
    },
    charger(start, end, callBack) {
      // `ajax` bascule en POST dès qu'on lui passe des paramètres : ici, null.
      this.$api.ajax('/client/parcours?start=' + start + '&end=' + end, null, res => callBack(this.extraire(res)));
    },
    init() {
      if (this.load) return false;

      // start et end sont obligatoires côté back : on valide avant d'appeler.
      const debut = this.$api.moment(this.filters.start, 'YYYY-MM-DD', true);
      const fin   = this.$api.moment(this.filters.end, 'YYYY-MM-DD', true);
      if (!this.filters.start || !this.filters.end || !debut.isValid() || !fin.isValid()) {
        this.dateError = "Les deux dates sont obligatoires.";
        return false;
      }
      if (debut.isAfter(fin)) {
        this.dateError = "La date de début est postérieure à la date de fin.";
        return false;
      }
      this.dateError = null;

      // Période précédente : même nombre de jours, qui se termine la veille
      // du début. 7 jours du 16 au 22 → comparés au 9 au 15.
      const nbJours = fin.diff(debut, 'days') + 1;
      const precFin = debut.clone().subtract(1, 'day');
      const precDebut = precFin.clone().subtract(nbJours - 1, 'days');
      this.periodePrec = { start: precDebut.format('YYYY-MM-DD'), end: precFin.format('YYYY-MM-DD') };

      this.load = true;
      let restants = 2;
      const termine = () => { restants -= 1; if (restants === 0) this.load = false; };

      this.charger(this.filters.start, this.filters.end, events => {
        if (events === null) {
          this.events = [];
          this.$bvToast.toast("Les statistiques du parcours n'ont pas pu être chargées.", {
            title: 'Erreur', variant: 'danger', solid: true
          });
        } else {
          this.events = events;
        }
        termine();
      });
      // Un échec ici n'est pas bloquant : les KPI s'affichent sans écart.
      this.charger(this.periodePrec.start, this.periodePrec.end, events => {
        this.eventsPrec = events;
        termine();
      });
    }
  },
  mounted() {
    this.init();
  }
}
</script>

<style scoped>
.sous-titre {
  font-size: 0.8rem;
}
.legende {
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.legende-discrete {
  font-size: 0.72rem;
}
.filtre-actif {
  font-size: 0.85rem;
}

/* KPI : grille plutôt que colonnes Bootstrap — 7 tuiles ne se répartissent
   pas sur 12 colonnes. */
.kpi-grille {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}
.kpi-carte {
  height: 100%;
}
.kpi-titre {
  font-size: 0.72rem;
  letter-spacing: 0.04em;
}
.kpi-valeur {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
}
.kpi-detail {
  font-size: 0.75rem;
}
.kpi-delta {
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.4rem;
}
.comparaison-legende {
  font-size: 0.72rem;
  margin-top: 0.5rem;
}

/* Barres (funnel, détails, répartitions) */
.piste {
  background: #f3f6f9;
  border-radius: 4px;
  height: 14px;
  overflow: hidden;
  margin-top: 0.25rem;
}
.piste-fine {
  height: 6px;
  background: #e7ebf0;
}
.barre {
  height: 100%;
  border-radius: 4px;
  transition: width 0.35s ease;
}

/* Funnel */
.etape-cliquable {
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
  transition: background 0.15s ease;
}
.etape-cliquable:hover {
  background: #f8f9fb;
}
.etape-chevron {
  font-size: 0.65rem;
  opacity: 0.5;
}
.detail-etape {
  background: #f8f9fb;
  border-radius: 4px;
  padding: 0.75rem 0.9rem;
  font-size: 0.82rem;
}
.detail-ligne + .detail-ligne {
  margin-top: 0.6rem;
}

/* Jour par jour */
.jours-zone {
  overflow-x: auto;
}
.jours {
  display: flex;
  align-items: flex-end;
  min-height: 220px;
}
.jour {
  flex: 1 0 44px;
  text-align: center;
  padding: 0 2px;
}
.jour-barres {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 160px;
}
.jour-barre {
  width: 38%;
  margin: 0 2px;
  border-radius: 3px 3px 0 0;
  transition: height 0.35s ease;
}
.jour-taux {
  font-size: 0.72rem;
  font-weight: 700;
  margin-top: 0.25rem;
}
.jour-label {
  font-size: 0.7rem;
  white-space: nowrap;
}
.legende-puce {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  vertical-align: middle;
  margin-right: 0.35rem;
}

/* Visites */
.message-cle {
  background: #fff8e6;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  font-size: 0.85rem;
}
.colonne-demandes {
  width: 40%;
}
.valeur-demande {
  min-width: 70px;
  text-align: right;
}
.non-significatif td {
  color: #9aa1a9;
}

/* Carte de chaleur */
.heatmap {
  font-size: 0.68rem;
  overflow-x: auto;
}
.hm-ligne {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}
.hm-jour {
  flex: 0 0 34px;
  color: #6c757d;
}
.hm-heure {
  flex: 1 0 14px;
  color: #6c757d;
  text-align: left;
  white-space: nowrap;
}
.hm-cellule {
  flex: 1 0 14px;
  height: 22px;
  margin-right: 2px;
  border-radius: 2px;
  background: #f3f6f9;
  overflow: hidden;
}
.hm-remplissage {
  display: block;
  width: 100%;
  height: 100%;
}

/* Appareils */
.icone-appareil {
  width: 14px;
  text-align: center;
}

/* Conseillers */
.equipe-ligne {
  font-size: 0.82rem;
  background: #f8f9fb;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}
.taux-grise {
  cursor: help;
  border-bottom: 1px dotted #adb5bd;
}
</style>

<style>
/* Non scopés : b-table et b-tabs posent ces classes sur leurs propres nœuds. */
.ligne-cliquable {
  cursor: pointer;
}
.ligne-choisie td {
  background: #eef4ff !important;
  font-weight: 600;
}
.onglets .nav-link {
  font-weight: 600;
}
@media print {
  .menu-desktop, #menu-mobile, #header {
    display: none !important;
  }
}
</style>
